import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle, 
  Circle, 
  ArrowRight, 
  Play,
  Target,
  Users,
  MessageSquare,
  Sparkles,
  Calendar,
  UserCircle,
  TrendingUp,
  Clock
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useNavigate } from "react-router-dom";
import { usersService } from "@/services/usersService";
import { wellnessService } from "@/services/wellnessService";
import { goalsService } from "@/services/goalsService";

interface GuideStep {
  id: string;
  title: string;
  description: string;
  actionText: string;
  icon: React.ReactNode;
  estimatedTime: string;
  completed: boolean;
  onClick: () => void;
  priority: 'high' | 'medium' | 'low';
}

interface PostSignupGuideProps {
  onComplete?: () => void;
  onDismiss?: () => void;
}

export default function PostSignupGuide({ onComplete, onDismiss }: PostSignupGuideProps) {
  const { user } = useUser();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<GuideStep[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Check if guide was dismissed
  useEffect(() => {
    if (!user?.id) return;
    
    const checkDismissed = async () => {
      try {
        const progressResponse = await usersService.getProgress(user.id);
        if (progressResponse.success && progressResponse.data?.guide?.guide_dismissed) {
          setIsVisible(false);
        }
      } catch (error) {
        // If error, show guide by default
        console.error('Error checking guide dismissal:', error);
      }
    };
    
    checkDismissed();
  }, [user?.id]);

  // Initialize guide steps
  useEffect(() => {
    if (!user?.id) return;

    const loadStepStatus = async () => {
      try {
        // Check if user has completed basic actions
        const [profileData, checkInsData, goalsData, progressData] = await Promise.all([
          // Check if profile is complete
          usersService.getUserById(user.id).catch(() => null),
          
          // Check if user has any check-ins
          wellnessService.getCheckIns({ limit: 1 }).catch(() => ({ success: false, data: { checkIns: [] } })),
            
          // Check if user has any goals
          goalsService.getGoals().catch(() => ({ success: false, data: [] })),
          
          // Get user progress for visited pages
          usersService.getProgress(user.id).catch(() => ({ success: false, data: {} }))
        ]);

        // DEFINITION OF DONE for each guide step:
        // 1. Complete Profile: User has name (display_name or displayName), bio, AND avatar filled in their profile (essential fields)
        const profile = profileData?.success ? profileData.data : profileData;
        const hasCompleteProfile = (profile?.displayName || profile?.display_name) && 
                                  profile?.bio && 
                                  (profile?.avatarUrl || profile?.avatar_url);
        
        // 2. First Check-in: User has completed at least one check-in
        const checkIns = Array.isArray(checkInsData) 
          ? checkInsData 
          : (checkInsData?.data?.checkIns || checkInsData?.data || []);
        const hasCheckIns = checkIns.length > 0;
        
        // 3. Set Goal: User has created at least one goal
        const goals = goalsData?.success && goalsData?.data 
          ? (Array.isArray(goalsData.data) ? goalsData.data : [])
          : [];
        const hasGoals = goals.length > 0;
        
        // 4. Explore AI Insights: User has visited the /patterns page (tracked in backend UserProgress)
        const progress = progressData?.success ? progressData.data : {};
        const visitedPages = progress?.visited_pages?.pages || [];
        const hasExploredInsights = visitedPages.includes('patterns');
        
        // 5. Connect with Team: User has visited the /team page (tracked in backend UserProgress)
        const hasConnectedWithTeam = visitedPages.includes('team');

        const guideSteps: GuideStep[] = [
          {
            id: 'first-checkin',
            title: 'Complete Your First Check-in',
            description: 'Start tracking your daily energy, mood, and progress. This is the foundation of your Step experience.',
            actionText: 'Start First Check-in',
            icon: <CheckCircle className="h-5 w-5" />,
            estimatedTime: '2-3 minutes',
            completed: hasCheckIns,
            priority: 'high',
            onClick: () => navigate('/checkin')
          },
          {
            id: 'complete-profile',
            title: 'Set Up Your Profile',
            description: 'Add your name, bio, and profile picture for a complete profile.',
            actionText: 'Complete Profile',
            icon: <UserCircle className="h-5 w-5" />,
            estimatedTime: '3-5 minutes',
            completed: hasCompleteProfile,
            priority: 'high',
            onClick: () => {
              // Trigger the ProfileSettingsWidget to open
              const settingsButton = document.querySelector('[data-testid="profile-settings-trigger"]') as HTMLButtonElement;
              if (settingsButton) {
                settingsButton.click();
              } else {
                // Fallback: navigate to settings page
                navigate('/settings');
              }
            }
          },
          {
            id: 'set-first-goal',
            title: 'Set Your First Goal',
            description: 'Define what you want to achieve. Goals help you stay focused and track meaningful progress.',
            actionText: 'Create Goal',
            icon: <Target className="h-5 w-5" />,
            estimatedTime: '5-10 minutes',
            completed: hasGoals,
            priority: 'high',
            onClick: () => navigate('/profile') // Navigate to My Growth page where goals are managed
          },
          {
            id: 'explore-insights',
            title: 'Discover AI Insights',
            description: 'Learn how Step analyzes your patterns and provides personalized recommendations.',
            actionText: 'View Insights',
            icon: <Sparkles className="h-5 w-5" />,
            estimatedTime: '2-3 minutes',
            completed: hasExploredInsights,
            priority: 'medium',
            onClick: async () => {
              navigate('/patterns');
              // Mark as visited in backend
              await markPageVisited('patterns');
              markStepCompleted('explore-insights');
            }
          },
          {
            id: 'connect-team',
            title: 'Connect with Your Team',
            description: 'Join your team workspace to collaborate, share progress, and stay connected.',
            actionText: 'Explore Team Features',
            icon: <Users className="h-5 w-5" />,
            estimatedTime: '5 minutes',
            completed: hasConnectedWithTeam,
            priority: user?.persona === 'independentSolo' ? 'low' : 'medium',
            onClick: async () => {
              if (user?.persona === 'independentSolo') {
                navigate('/subscribe');
              } else {
                navigate('/team');
                // Mark as visited in backend
                await markPageVisited('team');
              }
              markStepCompleted('connect-team');
            }
          }
        ];

        setSteps(guideSteps);
        
        // Set current step to first incomplete high-priority step
        const firstIncomplete = guideSteps.findIndex(step => !step.completed && step.priority === 'high');
        setCurrentStep(firstIncomplete >= 0 ? firstIncomplete : 0);

      } catch (error) {
        console.error('Error loading guide status:', error);
      }
    };

    loadStepStatus();
  }, [user?.id, user?.persona, navigate]);

  const markPageVisited = async (page: string) => {
    if (!user?.id) return;
    
    try {
      // Get current progress
      const progressResponse = await usersService.getProgress(user.id);
      const currentProgress = progressResponse.success ? progressResponse.data : {};
      const visitedPages = currentProgress?.visited_pages?.pages || [];
      
      // Add page if not already visited
      if (!visitedPages.includes(page)) {
        const updatedPages = [...visitedPages, page];
        await usersService.updateProgress(user.id, {
          type: 'visited_pages',
          data: { pages: updatedPages }
        });
      }
    } catch (error) {
      console.error('Error marking page as visited:', error);
    }
  };

  const markStepCompleted = async (stepId: string) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: true } : step
    ));
    
    // Store completion in backend UserProgress
    if (user?.id) {
      try {
        const progressResponse = await usersService.getProgress(user.id);
        const currentProgress = progressResponse.success ? progressResponse.data : {};
        const completedSteps = currentProgress?.guide_steps?.completed || [];
        
        if (!completedSteps.includes(stepId)) {
          await usersService.updateProgress(user.id, {
            type: 'guide_steps',
            data: { completed: [...completedSteps, stepId] }
          });
        }
      } catch (error) {
        console.error('Error saving step completion:', error);
      }
    }
  };

  const completedSteps = steps.filter(step => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  const handleNext = () => {
    const nextIncompleteStep = steps.findIndex((step, index) => 
      index > currentStep && !step.completed
    );
    
    if (nextIncompleteStep >= 0) {
      setCurrentStep(nextIncompleteStep);
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // All steps completed or reached the end
      onComplete?.();
    }
  };

  const handleSkipToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  const handleDismiss = async () => {
    setIsVisible(false);
    
    // Store dismissal in backend UserProgress
    if (user?.id) {
      try {
        await usersService.updateProgress(user.id, {
          type: 'guide',
          data: { guide_dismissed: true }
        });
      } catch (error) {
        console.error('Error saving guide dismissal:', error);
      }
    }
    
    onDismiss?.();
  };

  if (!isVisible || steps.length === 0) return null;

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Play className="h-6 w-6 text-primary" />
                Welcome to Step!
              </CardTitle>
              <CardDescription className="mt-2">
                Let's get you started with a quick 10-minute setup to unlock your best work experience.
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleDismiss}>
              Skip Setup
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2 mt-4">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{completedSteps} of {totalSteps} completed</span>
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Current Step Highlight */}
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  {currentStepData?.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{currentStepData?.title}</h3>
                    <Badge variant={currentStepData?.priority === 'high' ? 'default' : 'secondary'}>
                      {currentStepData?.priority === 'high' ? 'Essential' : 'Recommended'}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground mb-4">{currentStepData?.description}</p>
                  <div className="flex items-center gap-4">
                    <Button 
                      onClick={currentStepData?.onClick}
                      className="flex items-center gap-2"
                      disabled={currentStepData?.completed}
                    >
                      {currentStepData?.completed ? (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Completed
                        </>
                      ) : (
                        <>
                          {currentStepData?.actionText}
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {currentStepData?.estimatedTime}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Step Overview */}
          <div>
            <h4 className="font-medium mb-3">Setup Checklist</h4>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div 
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    index === currentStep 
                      ? 'bg-primary/10 border border-primary/20' 
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => handleSkipToStep(index)}
                >
                  {step.completed ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${step.completed ? 'text-green-700' : ''}`}>
                        {step.title}
                      </span>
                      {step.priority === 'high' && !step.completed && (
                        <Badge variant="secondary" className="text-xs">Essential</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{step.estimatedTime}</p>
                  </div>
                  {index === currentStep && (
                    <Badge variant="outline">Current</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={handleDismiss}>
              I'll do this later
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  Previous
                </Button>
              )}
              <Button onClick={handleNext}>
                {currentStep === steps.length - 1 ? 'Finish Setup' : 'Next Step'}
              </Button>
            </div>
          </div>

          {/* Quick Tips */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h5 className="font-medium text-blue-900 mb-2">💡 Quick Tip</h5>
              <p className="text-sm text-blue-800">
                {currentStepData?.id === 'first-checkin' && "Your first check-in helps establish your baseline. Be honest about how you're feeling right now!"}
                {currentStepData?.id === 'complete-profile' && "A complete profile helps your team understand your working style and preferences."}
                {currentStepData?.id === 'set-first-goal' && "Start with a small, achievable goal. You can always add more ambitious ones later!"}
                {currentStepData?.id === 'explore-insights' && "Insights become more accurate as you complete more check-ins. Your patterns will emerge over time."}
                {currentStepData?.id === 'connect-team' && "Team features help you stay connected and support each other's growth and wellbeing."}
              </p>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}